import type { ErrorEvent } from "@sentry/react";

/**
 * Browser error tracking. Off without `VITE_SENTRY_DSN`, so local and test runs
 * send nothing.
 *
 * The SDK is loaded only when a DSN exists, so a build without one pays nothing
 * for it on a 2G connection.
 *
 * URLs are trimmed to their path and every request body, cookie and user field
 * is dropped. Routes carry tokens (`/p/consent/:token`, `/p/pay/:reference`)
 * and the app holds student answers and emails; none of it goes to a third
 * party.
 */
const TOKEN_ROUTES = /^(\/p\/(?:consent|pay|progress))\/[^/?#]+/;

export function scrubUrl(url: string | undefined): string | undefined {
  if (!url) return url;
  try {
    const parsed = new URL(url, "https://app.invalid");
    return parsed.pathname.replace(TOKEN_ROUTES, "$1/:token");
  } catch {
    return undefined;
  }
}

export function scrubEvent<T extends ErrorEvent>(event: T): T {
  if (event.request) {
    delete event.request.data;
    delete event.request.cookies;
    delete event.request.headers;
    delete event.request.query_string;
    const url = scrubUrl(event.request.url);
    if (url) event.request.url = url;
    else delete event.request.url;
  }
  delete event.user;
  return event;
}

export async function initErrorTracking(
  dsn: string | null | undefined = import.meta.env.VITE_SENTRY_DSN,
): Promise<boolean> {
  if (!dsn) return false;

  // A separate chunk, fetched only when a DSN is configured.
  const Sentry = await import("@sentry/react");
  Sentry.init({
    dsn,
    environment: import.meta.env.VITE_APP_ENV ?? "local",
    dataCollection: {
      userInfo: false,
      cookies: false,
      httpHeaders: false,
      httpBodies: [],
      urlQueryParams: false,
    },
    tracesSampleRate: 0,
    beforeSend: (event) => scrubEvent(event),
    beforeBreadcrumb: (breadcrumb) => {
      // Navigation and fetch breadcrumbs carry full URLs, tokens included.
      if (breadcrumb.data?.url) breadcrumb.data.url = scrubUrl(String(breadcrumb.data.url));
      if (breadcrumb.data?.to) breadcrumb.data.to = scrubUrl(String(breadcrumb.data.to));
      if (breadcrumb.data?.from) breadcrumb.data.from = scrubUrl(String(breadcrumb.data.from));
      return breadcrumb;
    },
  });
  return true;
}
